import React, { useMemo, useState, useEffect, useCallback } from "react";
import { useForm } from "react-hook-form";
import { cloneDeep, omit, orderBy, set } from "lodash";

import useTranslations from "../../../hooks/useTranslations";

import { FormStepProps } from "../../../screens/ReviewFormScreen";
import { Button, Container, TailSpin, ScreenHeader } from "../../wombat-ui";
import { ReviewIcon, ArrowRightIcon, CloseIcon } from "../../icons";

import { TemplatePreviewCard } from "./TemplatePreviewModal";

import TemplateForm from "../../templates/TemplateForm";

import { DEFAULT_TEMPLATE } from "../../../screens/TemplateFormScreen";
import { StringParam, useQueryParam } from "use-query-params";
import { TemplateFragment, useGetTemplates } from '../../../graphql/generated/definitions';

type NextStepButtonControlProps = {
  formState: ReturnType<typeof useForm>["formState"];
  onNext: () => void;
};

const NextStepButtonControl: React.FC<NextStepButtonControlProps> = React.memo(
  ({ formState, onNext }) => {
    const t = useTranslations();
    const disabled = useMemo(() => {
      return formState.errors.templateAttributes !== undefined;
    }, [formState]);

    return (
      <Button
        disabled={disabled}
        onClick={onNext}
        id="selectTemplateButton"
        trailingIcon={ArrowRightIcon}
      >
        {t("forms.actions.next")}
      </Button>
    );
  }
);

const InnerStep: React.FC<
  FormStepProps & {
    template: TemplateFragment;
    onTemplateChange: () => void;
  }
> = React.memo(
  ({
    register,
    unregister,
    setValue,
    getValues,
    trigger,
    template,
    control,
    formState,
    errors,
    onTemplateChange,
    onNext,
    reset,
  }) => {
    const t = useTranslations();
    const [valuesSet, setValuesSet] = useState(false);

    useEffect(() => {
      const { sections: templateSections, ...templateAttributes } =
        template || DEFAULT_TEMPLATE;

      let sections = templateSections.map(({ questions, ...ts }) => ({
        ...ts,
        questionsAttributes: questions,
      }));

      sections = orderBy(
        sections.map((e) => omit(e, "id", "__typename")),
        "order",
        "asc"
      );
      sections.forEach((section) => {
        section.questionsAttributes = orderBy(
          section.questionsAttributes.map((questionAttributes) => ({
            ...omit(
              questionAttributes,
              "__typename",
              "id",
              "optionsSchema",
              "defaultValue",
              "defaultValueSchema"
            ),
          })),
          "order",
          "asc"
        );
      });

      setValue(
        "templateAttributes",
        cloneDeep({
          ...templateAttributes,
          sectionsAttributes: sections,
        }),
        { shouldValidate: true }
      );

      // Okay this bit is rather ugly but I couldn't find any other way to
      // set the values and trigger a propre rerender, a simple set on the
      // same tick wouldn't cut it
      setTimeout(() => setValuesSet(true));
    }, [template]);

    if (!valuesSet) {
      return null;
    }

    return (
      <>
        <ScreenHeader
          icon={ReviewIcon}
          title={t("reviews.new")}
          description={template?.title || t("reviews.customFollowUp")}
          actions={[
            <Button
              variant="subtle"
              onClick={onTemplateChange}
              key="back"
              id="changeTemplateButton"
              leadingIcon={CloseIcon}
            >
              {t("reviews.changeTemplate")}
            </Button>,
            <NextStepButtonControl
              onNext={onNext}
              formState={formState}
              key="next"
            />,
          ]}
        />
        <Container className={`flex-grow w-full`}>
          <div className="flex flex-col space-y-4">
            <TemplateForm
              name="templateAttributes"
              errors={errors?.templateAttributes}
              {...{
                register,
                unregister,
                setValue,
                getValues,
                trigger,
                control,
                formState,
                reset,
              }}
            />
          </div>
        </Container>
      </>
    );
  }
);

const SecondStep: React.FC<FormStepProps> = ({
  onNext,
  register,
  unregister,
  setValue,
  trigger,
  getValues,
  formState,
  control,
  errors,
  reset,
}) => {
  const t = useTranslations();

  const { data, loading } = useGetTemplates({
    variables: { reviewId: null, sortBy: "createdAtWithFavoriteFirst" },
  });
  const templates = data?.templates?.collection || [];

  const [innerStep, setInnerStep] = useState(false);
  const [selectedTemplate, setSelectedTemplate] =
    useState<TemplateFragment | null>(null);

  /**
   * Utility callback to switch templates
   */
  const [selectedTemplateId, setSelectedTemplateId] = useQueryParam(
    "template",
    StringParam
  );

  useEffect(() => {
    // If we have a template already selected
    if (selectedTemplateId === "custom") {
      setSelectedTemplate(null);
      setInnerStep(true);
    } else if (selectedTemplateId) {
      const selected = templates?.find(
        (template) => template.slug === selectedTemplateId
      );
      // If the id resolve to a template
      if (selected) {
        setSelectedTemplate(selected);
        setInnerStep(true);
      }
    } else {
      // Clean up the old template form
      const values = cloneDeep(getValues());
      set(values, "templateAttributes", {});
      reset(values);

      setSelectedTemplate(null);
      setInnerStep(false);
    }
  }, [selectedTemplateId, templates]);

  const handleSetCustomFollowUp = useCallback(() => {
    setSelectedTemplateId("custom");
  }, [setSelectedTemplateId]);

  if (!innerStep)
    return (
      <>
        <ScreenHeader
          icon={ReviewIcon}
          title={t("reviews.new")}
          description={t("reviews.content")}
          actions={[
            <Button
              onClick={handleSetCustomFollowUp}
              variant="subtle"
              key="customFollowUp"
              id="customFollowUpButton"
            >
              {t("reviews.customFollowUp")}
            </Button>,
          ]}
        />
        <Container className={`flex-col flex-grow w-full`}>
          <div className="flex flex-col space-y-6">
            {!loading && (
              <ul className="grid grid-cols-1 gap-6 sm:grid-cols-2 lg:grid-cols-3">
                {templates.map((template) => (
                  <TemplatePreviewCard
                    template={template}
                    key={template.id}
                    onSelect={setSelectedTemplateId}
                  />
                ))}
              </ul>
            )}
            {loading && (
              <div className="flex flex-col items-center justify-center flex-grow">
                <TailSpin className="w-8 h-8" />
              </div>
            )}
          </div>
        </Container>
      </>
    );

  return (
    <InnerStep
      template={selectedTemplate}
      onTemplateChange={() => setSelectedTemplateId(null)}
      {...{
        register,
        unregister,
        setValue,
        getValues,
        control,
        trigger,
        onNext,
        errors,
        formState,
        reset,
      }}
    />
  );
};

export default React.memo(SecondStep);
